import { STORE_KEYS } from '@constants'
import { saveToStorage } from '@helpers/storage.helpers'
import { Store } from '@stores'
import dayjs from 'dayjs'
import { action, makeAutoObservable } from 'mobx'
import i18n from 'src/i18n'

type IYearOptionsArray = { label: string; value: string }[]

export interface IDashboardData {
  year: number
  quarter: number | null
  revenue: {
    total_revenue_excl_vat: number
    total_vat: number
    total_revenue_incl_vat: number
    invoice_count: number
    average_invoice_value: number
  }
  monthly_revenue: Array<{
    month: number
    revenue: number
    vat: number
    total: number
    count: number
  }>
  quotations: {
    total_quotations: number
    finalised_quotations: number
    draft_quotations: number
    quotation_value: number
  }
  top_clients: Array<{
    client_id: number
    client_name: string
    total_revenue: number
    invoice_count: number
  }>
  recent_documents: Array<{
    id: number
    type: string
    type_label: string
    number: string
    client_name: string
    amount: number
    finalised_at: string
  }>
  overdue_invoices: Array<{
    id: number
    document_number: string
    client_name: string
    amount: number
    expires_at: string
    days_overdue: number
  }>
  invoice_status: {
    outstanding_count: number
    outstanding_value: number
    overdue_count: number
    overdue_value: number
    paid_count: number
    paid_value: number
  }
  client_stats: {
    total_clients: number
    clients_with_invoices: number
  }
  draft_invoices_count: number
  draft_quotations_count: number
}

export class DashboardStore {
  [key: string]: any
  store: Store
  data: IDashboardData | null
  selectedYear: number
  selectedQuarter: null | 1 | 2 | 3 | 4
  loading: boolean

  constructor(store: Store) {
    this.store = store
    this.data = null
    this.selectedYear = dayjs().year()
    this.selectedQuarter = null
    this.loading = true
    makeAutoObservable(this)
  }

  createYearOptions = (): IYearOptionsArray => {
    const current_year = dayjs().year()
    const first_option = dayjs().subtract(2, 'year').year()
    const options: any = new Set([])

    for (let i = first_option; i < current_year + 1; i++) {
      const option = {
        value: i,
        label: `${i}`,
      }

      options.add(option)
    }

    return Array.from(options).reverse() as IYearOptionsArray
  }

  createQuarterOptions = () => {
    return [
      { value: 'full', label: 'Volledig jaar' },
      { value: 1, label: 'Q1 (Jan - Mrt)' },
      { value: 2, label: 'Q2 (Apr - Jun)' },
      { value: 3, label: 'Q3 (Jul - Sep)' },
      { value: 4, label: 'Q4 (Okt - Dec)' },
    ]
  }

  @action
  handleSelectedYear = (year: number) => {
    this.set('selectedYear', year)
    this.getData()
  }

  @action
  handleSelectedQuarter = (quarter: null | number) => {
    this.set('selectedQuarter', quarter)
    this.getData()
  }

  @action
  getData = async () => {
    this.set(STORE_KEYS.LOADING, true)
    let params: any = {
      year: this.selectedYear,
    }

    if (this.selectedQuarter) {
      params = { ...params, quarter: this.selectedQuarter }
    }

    try {
      const res = await this.store.api.dashboard.getData({ params })
      this.set('data', res)
    } catch (e) {
      console.log(e)
      this.store.ui.handleErrorToast(i18n.t('errors:dashboard_load_failed'))
    }
    this.set(STORE_KEYS.LOADING, false)
  }

  @action
  reset = () => {
    this.data = null
    this.selectedYear = dayjs().year()
    this.selectedQuarter = null
    this.loading = true
  }

  @action
  set = (target: string, value: unknown, save?: boolean): unknown => {
    if (!target) return
    this[target] = value
    if (save) saveToStorage(target, value)
  }
}
